// Spec: specs/stats/sidebar/filters_tab/respondent_uids/functional_requirements.md

import React from 'react'
import { render, unmountComponentAtNode } from 'react-dom'
import { act } from 'react-dom/test-utils'

// The gate under test is the only thing this file asserts, so every heavy child is stubbed down to a
// marker element — the panel body, the advanced editor and the basic groups are covered elsewhere.
jest.mock('@aytm/app/stats/page/sidebar/filters/FiltersUids', () => ({
  __esModule: true,
  default: ({ uids, uidsTruncated }) => (
    <div data-testid="uids-stub" data-count={uids.length} data-truncated={String(uidsTruncated)} />
  )
}))

jest.mock('@aytm/app/stats/page/sidebar/Sidebar', () => ({ DataContext: require('react').createContext({}) }))
jest.mock('@aytm/app/stats/page/sidebar/filters/FiltersAdvanced', () => () => <div data-testid="advanced-stub" />)
jest.mock('@aytm/app/stats/page/sidebar/filters/FiltersGroup', () => () => null)
jest.mock('@aytm/app/stats/page/sidebar/filters/groups/FiltersGroupCompletes', () => () => null)
jest.mock('@aytm/app/stats/page/sidebar/components/SidebarPopupWarning', () => () => null)
jest.mock(
  '@aytm/app/stats/page/sidebar/components/SidebarPopup',
  () =>
    ({ children }) =>
      children
)
jest.mock('@aytm/lib/layout/container-expander', () => ({ __esModule: true, default: () => ({ expand: () => {} }) }))
jest.mock('@aytm/app/stats/lib/query/refs', () => ({ getLogicData: () => [] }))
jest.mock('@aytm/app/stats/lib/query/schema-rules', () => ({ __esModule: true, default: { flatten: () => ({}) } }))
jest.mock('@aytm/app/stats/lib/query/basic-filters', () => ({
  __esModule: true,
  default: class BasicFiltersStub {
    convertToBasic() {
      return {}
    }

    convertToAdvanced() {
      return ''
    }
  }
}))
jest.mock('@aytm/app/stats/page/sidebar/filters/converter', () => ({
  renderFilters: () => [],
  renderSurveySchema: () => ({})
}))

import FiltersPanel from './FiltersPanel'
import { DataContext as SidebarDataContext } from '@aytm/app/stats/page/sidebar/Sidebar'

window.HTMLElement.prototype.scroll = function () {}

// `errors` drives parserResultPatternError, which is what forces the basic parse to null.
const makeParser = (errors = []) => ({
  analyzeString: () => ({ expression: { status: { errors, codebooks: {} }, tree: {} } })
})

const FEATURES = {
  filters: { disabled: false, hidden: false },
  advanced_filters: { disabled: false, hidden: false }
}

let container = null

beforeEach(() => {
  container = document.createElement('div')
  document.body.appendChild(container)
})

afterEach(() => {
  unmountComponentAtNode(container)
  container.remove()
  container = null
})

const mount = ({ parserErrors = [], features = FEATURES, ...model } = {}) => {
  act(() => {
    render(
      <SidebarDataContext.Provider value={{ sidebarState: {}, sidebarDispatch: () => {} }}>
        <FiltersPanel
          survey={{}}
          model={{
            mode: 'charts',
            isAdmin: true,
            filtersMode: 'advanced',
            filtersPattern: 'q1a1',
            reportUids: [1, 2, 3],
            reportUidsTruncated: false,
            ...model
          }}
          report={{ current: { schema: [], virtual: {} } }}
          parser={makeParser(parserErrors)}
          features={features}
          onParamsChange={() => {}}
          onModeChange={() => {}}
        />
      </SidebarDataContext.Provider>,
      container
    )
  })
}

const uidsSurface = () => container.querySelector('[data-testid="uids-stub"]')

describe('FiltersPanel — Show UUIDs visibility gate', () => {
  it('renders the surface for an internal viewer with the Advanced editor showing and a filter applied', () => {
    mount()

    expect(uidsSurface()).not.toBeNull()
  })

  it('withholds it from a non-internal viewer', () => {
    mount({ isAdmin: false })

    expect(uidsSurface()).toBeNull()
  })

  it('withholds it while the panel is not interactive', () => {
    mount({ features: { ...FEATURES, filters: { disabled: true, hidden: false } } })

    expect(uidsSurface()).toBeNull()
  })

  it('withholds it when no filter is applied', () => {
    mount({ filtersPattern: '' })

    expect(uidsSurface()).toBeNull()
  })

  it('withholds it while the Basic editor is showing', () => {
    mount({ filtersMode: 'basic' })

    expect(uidsSurface()).toBeNull()
  })

  it('renders it in Basic mode when the applied filter has no basic parse', () => {
    mount({ filtersMode: 'basic', parserErrors: [{ type: 'syntax' }] })

    expect(uidsSurface()).not.toBeNull()
  })

  it('passes the loaded uids straight through', () => {
    mount({ reportUids: [11, 22], reportUidsTruncated: true })

    expect(uidsSurface().dataset.count).toBe('2')
    expect(uidsSurface().dataset.truncated).toBe('true')
  })
})
